<template>
  <FormGroup>
    <!-- A building sets no rent: each unit has its own -->
    <FormField v-if="!forBuilding" label="Monthly rent" :error="form.fieldErrors('rent')">
      <Input v-model="form.fields.rent" type="number" min="0">
        <template #prefix>$</template>
      </Input>
    </FormField>

    <FormField label="Security deposit" :error="form.fieldErrors('securityDeposit')">
      <Select v-model="form.fields.securityDeposit">
        <option v-for="option in incomeOptions" :key="option" :value="option">{{ option }}</option>
      </Select>
    </FormField>

    <FormField label="Application provider" :error="form.fieldErrors('applicationProvider')">
      <Select v-model="form.fields.applicationProvider">
        <option v-for="option in applicationProviderOptions" :key="option" :value="option">{{ option }}</option>
      </Select>
    </FormField>

    <template v-if="form.fields.applicationProvider !== ApplicationProvider.TALLO">
      <FormField label="Application fee" :error="form.fieldErrors('applicationFee')">
        <Input v-model="form.fields.applicationFee" type="number" min="0">
          <template #prefix>$</template>
        </Input>
      </FormField>

      <FormField label="Application co-signer fee (if applicable)" :error="form.fieldErrors('applicationCoSignerFee')">
        <Input v-model="form.fields.applicationCoSignerFee" type="number" min="0">
          <template #prefix>$</template>
        </Input>
      </FormField>
    </template>

    <FormField label="Other fees (optional)">
      <Textarea
        v-model="form.fields.otherFees"
        rows="3"
        placeholder="Please include all additional fees you may charge (admin, move-in, utilities, etc.) so renters are fully informed."
      />
    </FormField>
  </FormGroup>
</template>

<script setup lang="ts">
import { minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { computed, reactive } from 'vue';

import { ApplicationInviteType, applicationPrice } from '@tallo/applications';
import { FormField, FormGroup, Input, Select, Textarea, useForm } from '@tallo/design-system';
import { ApplicationProvider, LeaseConditionIncome } from '@tallo/property';

import { useDetailsHolder } from '../../composables/details-holder';
import { SaveLeaseConditionsDto } from '../../dtos/save-lease-condition.dto.interface';

type Fields = Pick<
  SaveLeaseConditionsDto,
  'rent' | 'securityDeposit' | 'applicationFee' | 'applicationCoSignerFee' | 'applicationProvider' | 'otherFees'
>;

const applicationProviderOptions = Object.values(ApplicationProvider);
const incomeOptions = Object.values(LeaseConditionIncome);

const holder = useDetailsHolder();
/** A building's row is what new units start with; the rent is each unit's own and never on the building */
const forBuilding = holder.kind === 'building';
const current = holder.details?.leaseConditions;

// The fields stand apart from the form, so the fee rules can read the provider without a cycle
const fields = reactive<Fields>({
  // A unit born in a building carries a rent of 0 until its own is set; the field starts empty rather than at 0
  rent: current?.rent || null,
  otherFees: current?.otherFees ?? null,
  securityDeposit: current?.securityDeposit ?? LeaseConditionIncome.X1_RENT,
  applicationProvider: current?.applicationProvider ?? ApplicationProvider.TALLO,
  applicationFee: current?.applicationFee ?? applicationPrice.get(ApplicationInviteType.APPLICANT),
  applicationCoSignerFee: current?.applicationCoSignerFee ?? applicationPrice.get(ApplicationInviteType.CO_SIGNER),
});
const applicationProvider = computed(() => fields.applicationProvider);
const feesRequired = () => applicationProvider.value !== ApplicationProvider.TALLO;

const form = useForm<Fields>(fields, {
  rent: forBuilding ? {} : { required, numeric, minValueValue: minValue(1) },
  applicationProvider: { required },
  applicationFee: { required: requiredIf(feesRequired), numeric, minValueValue: minValue(0) },
  applicationCoSignerFee: { required: requiredIf(feesRequired), numeric, minValueValue: minValue(0) },
});

async function save() {
  return form.submit(async (fields) => {
    const { rent, ...rest } = fields;
    const payload: SaveLeaseConditionsDto = forBuilding ? rest : { rent, ...rest };

    if (fields.applicationProvider === ApplicationProvider.TALLO) {
      delete payload.applicationFee;
      delete payload.applicationCoSignerFee;
    }

    await holder.saveLeaseConditions(payload);
  });
}

defineExpose({ save, form });
</script>
